import type { AppContext, AppProps, NextWebVitalsMetric } from 'next/app';
import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';

import SharedProvider from '@invideoio/web-shared/context/SharedProvider/SharedProvider';
import { AnalyticsType, PageType } from '@invideoio/web-shared/types/Analytics';
import { Analytics } from '@invideoio/web-shared/utils/analytics';
import { AnalyticsUtils } from '@invideoio/web-shared/utils/analytics/AnalyticsUtils';

import {
  BrowserType,
  PlatformType,
} from '@invideoio/web-shared/constants/common';
import { Auth } from '@invideoio/web-shared/lib/API/auth';
import { FeatureConfig } from '@invideoio/web-shared/types/Config';
import {
  LOCAL_STORAGE,
  URL_SEARCH_PARAM,
} from '@invideoio/web-shared/utils/utils.constant';
import type { BannerSpec } from '@invideoio/web-shared/components/WebinarBanner/banner-specs';
import { fetchConfig, fetchFeatureJson } from '@lib/API';
import { AnnouncementBannerContext } from '@store/AnnouncementBannerContext';
import store from '@store/store';
import '@styles/globals.scss';
import { AmplitudeEvents, sendAmplitudeAnalyticEvent } from '@utils/analytics';
import { getAnnouncementBanners } from '@utils/announcementBanners';
import { localStorageWithExpiry } from '@utils/localStorageUtils';
import App from 'next/app';
import dynamic from 'next/dynamic';
import { Inter } from 'next/font/google';
import { sendConsentLog } from '@utils/consentLog';
import { isGDPRRegion } from '@utils/geoDetect';
import {
  readConsentPreferences,
  writeConsentPreferences,
  dispatchConsentChanged,
} from '@invideoio/web-shared/utils/cookieConsent';
import { ConfigType, SocialLinksType } from '../types';

// If loading a variable font, you don't need to specify the font weight
const inter = Inter({
  subsets: ['latin'],
  weight: ['300', '400', '500', '600', '700', '800'],
  display: 'swap',
});

const SharedModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "SharedModal" */
      '@components/SharedModal/SharedModal'
    ),
  {
    ssr: false,
  },
);

const LoadAnalytics = dynamic(
  () =>
    import(
      /* webpackChunkName: "Header" */
      '@components/LoadAnalytics/LoadAnalytics'
    ),
  {
    ssr: false,
  },
);

const CookieConsentBanner = dynamic(
  () =>
    import('@invideoio/web-shared/components/CookieConsentBanner').then(
      mod => mod.CookieConsentBanner,
    ),
  { ssr: false },
);

export function reportWebVitals(metric: NextWebVitalsMetric) {
  console.log(metric);
}

export interface RootProps extends AppProps {
  config: ConfigType;
  featureConfig: FeatureConfig;
  platform: PlatformType;
  os: string;
  socialLinks: SocialLinksType;
  browser: BrowserType;
  userAgent: string;
  isGoogleBot: boolean;
  // Absent entirely when getInitialProps hits its catch block.
  announcementBanners?: BannerSpec[] | null;
}

interface MyWindow extends Window {
  nua?: string;
}

function RootApp({ Component, pageProps, ...otherProps }: RootProps) {
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    (window as unknown as MyWindow).nua = otherProps.userAgent;

    AnalyticsUtils.registerPage(PageType.TOOLS, {
      location: window.location.href,
    });
    sendAmplitudeAnalyticEvent(AmplitudeEvents.appLoad);

    const search_params = window.location.search;
    const params = new RegExp(Object.values(URL_SEARCH_PARAM).join('|'));

    if (search_params !== '' && params.test(search_params)) {
      localStorageWithExpiry.set('qs', search_params, 60 * 24);
    }

    const domain_referrer = document.referrer;
    if (
      domain_referrer !== '' &&
      !domain_referrer.match(/invideo\.io|localhost/)
    ) {
      localStorageWithExpiry.set('domain_referrer', domain_referrer, 60 * 24);
    }
  }, []);

  // Auto-accept cookies for visitors outside GDPR/consent-required regions.
  // Runs before dynamically-imported CookieConsentBanner and LoadAnalytics mount,
  // so they see the auto-written cookie and treat the user as a returning visitor.
  useEffect(() => {
    if (isGDPRRegion()) return; // GDPR region — let the banner handle it
    if (readConsentPreferences()) return; // Already has saved consent

    const prefs = {
      essential: true as const,
      analytics: true,
      marketing: true,
    };
    const cookieDomain =
      process.env.NEXT_PUBLIC_ENV === 'production' ? '.invideo.io' : undefined;
    writeConsentPreferences(prefs, cookieDomain);
    dispatchConsentChanged(prefs);
  }, []);

  useEffect(() => {
    if (localStorage.getItem(LOCAL_STORAGE.AUTH_TOKEN)) {
      Auth.getCompleteUserDetails().then(response => {
        const data = response.data;

        if (data) {
          setUserData(data);
        }
      });
    }
  }, []);

  return (
    <SharedProvider
      config={{
        RECAPTCHA_KEY: process.env.NEXT_PUBLIC_GOOGLE_CAPTCHA_KEY || '',
        siteConfig: otherProps.config,
        API: {
          ANALYTICS_API: process.env.NEXT_PUBLIC_ANALYTICS_API_URL || '',
          AUTH_API: process.env.NEXT_PUBLIC_AUTH_API || '',
          CORE_API: process.env.NEXT_PUBLIC_API_URL || '',
        },
        APP_ENV: process.env.NEXT_PUBLIC_ENV || 'prodcution',
        CDN_URL: {
          SHARED_URL: process.env.NEXT_PUBLIC_SHARED_CDN_URL || '',
        },
        HOST_URL: process.env.NEXT_PUBLIC_BASE_HOST || '',
        CREATE_PAGE_HOST_URL: process.env.NEXT_PUBLIC_CREATE_PAGE_HOST || '',
      }}
      user={userData}
      featureConfig={otherProps.featureConfig}
    >
      <Provider store={store}>
        <AnnouncementBannerContext.Provider
          value={otherProps.announcementBanners ?? null}
        >
          <main className={inter.className}>
            <Component {...pageProps} {...otherProps} />
          </main>
        </AnnouncementBannerContext.Provider>
        <SharedModal />
        <LoadAnalytics
          userData={userData}
          platform={otherProps.platform}
          isGoogleBot={otherProps.isGoogleBot}
        />
        <CookieConsentBanner
          cookieDomain={
            process.env.NEXT_PUBLIC_ENV === 'production'
              ? '.invideo.io'
              : undefined
          }
          policyUrl="/privacy-policy/"
          onBannerShown={() => {
            Analytics.send(AnalyticsType.RUDDER_STACK, {
              eventName: 'show_cookie_banner',
              eventProperties: {},
              userProperties: {},
            });
          }}
          onCtaClicked={(ctaType, preferences) => {
            sendConsentLog(ctaType, preferences);
            Analytics.send(AnalyticsType.RUDDER_STACK, {
              eventName: 'click_cookie_consent_cta',
              eventProperties: {
                cta_type: ctaType,
                cookie_consent: preferences,
              },
              userProperties: {},
            });
          }}
        />
      </Provider>
    </SharedProvider>
  );
}

RootApp.getInitialProps = async (appContext: AppContext): Promise<any> => {
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await App.getInitialProps(appContext);

  try {
    const deviceDetect = require('../middleware/deviceDetect').default;
    const { os, platform, browser, userAgent, isGoogleBot } =
      await deviceDetect(appContext.ctx);

    // Independent fetches — run them concurrently rather than serially.
    // `announcementBanners` is `null` when the CMS gave no answer (banner falls back
    // to its hardcoded slides) and `[]` when the CMS says there is no bar right now.
    const [config, featureConfig, announcementBanners] = await Promise.all([
      fetchConfig(),
      fetchFeatureJson(),
      getAnnouncementBanners(),
    ]);

    const socialLinks = config.social;

    return {
      ...appProps,
      os,
      platform,
      browser,
      userAgent,
      isGoogleBot,
      socialLinks,
      config,
      featureConfig,
      announcementBanners,
    };
  } catch (error) {
    console.error(error);
    return { ...appProps };
  }
};

export default RootApp;
